/**
 * Parse a free-text payment-terms label ("Net 30", "Due upon receipt") into a
 * day count. The one canonical parser: the web invoice editor, the schedule
 * generate-invoice dialog, and the API generation paths (billing run, schedule,
 * retainer drafts) all derive due dates through it. Returns undefined when the
 * label carries no recognizable day count; callers choose their own fallback.
 */
export const parsePaymentTermsDays = (terms?: string | null) => {
  if (!terms) return undefined
  const normalized = terms.toLowerCase()
  if (
    normalized.includes('due upon receipt') ||
    normalized.includes('due on receipt')
  ) {
    return 0
  }
  const netMatch = normalized.match(/net\s*(\d+)/)
  if (netMatch) {
    return Number(netMatch[1])
  }
  return undefined
}

export interface ParsedLateFeeTerms {
  /** Late-fee percentage, e.g. 5 for "5% after 15 days". */
  percent?: number
  /** Grace period in days, e.g. 15 for "5% after 15 days". */
  graceDays?: number
}

/**
 * Parse free-text late-fee terms ("5% after 15 days", "1.5% monthly") into
 * their numeric parts, in the style of parsePaymentTermsDays. Used by the
 * invoice editor's late-fee assist to SUGGEST an amount; the terms text
 * itself stays the document truth and unparseable text ("Per contract
 * terms") returns undefined so callers fall back to hand entry.
 */
export const parseLateFeeTerms = (
  terms?: string | null,
): ParsedLateFeeTerms | undefined => {
  if (!terms) return undefined
  const normalized = terms.toLowerCase()
  const percentMatch = normalized.match(/(\d+(?:\.\d+)?)\s*%/)
  const graceMatch = normalized.match(/after\s+(\d+)\s*days?/)
  if (!percentMatch && !graceMatch) return undefined
  return {
    ...(percentMatch ? { percent: Number(percentMatch[1]) } : {}),
    ...(graceMatch ? { graceDays: Number(graceMatch[1]) } : {}),
  }
}
